import React, { useEffect, useState, useCallback, useRef } from 'react';
import './LogicMaze.css';
import { useProgress } from '../contexts/ProgressContext';

const TOTAL_LEVELS = 6;

// Rank system
const RANKS = {
  0: "Script Kiddie",
  3: "White Hat",
  6: "Security Analyst",
  10: "Cryptographer",
  15: "Zero-Knowledge Master"
};

// Core validation function (shared across all themes)
function checkWin(level, playerState) {
  // For switch-based themes (Cyber Vault, Cipher), playerState is [L, M, R] boolean array
  // For Circuit Builder, playerState is the circuit configuration
  if (Array.isArray(playerState) && playerState.length === 3) {
    const [L, M, R] = playerState.map(Boolean);
  switch (level) {
    case 1: return (Number(L) + Number(M) + Number(R)) === 1;
    case 2: return (Number(L) + Number(M) + Number(R)) === 2;
    case 3: return !(L && R);
    case 4: return !(L && M && R);
    case 5: return !M || (L !== R);
      case 6: return (Number(L) + Number(M) + Number(R)) === 2 && R;
    default: return false;
  }
}
  // For Circuit Builder, we'll need to simulate the circuit
  // This will be implemented in the Circuit Builder theme
  return false;
}

// Get rank from streak
function getRankFromStreak(streak) {
  if (streak >= 15) return RANKS[15];
  if (streak >= 10) return RANKS[10];
  if (streak >= 6) return RANKS[6];
  if (streak >= 3) return RANKS[3];
  return RANKS[0];
}

// Theme Selector Component
const ThemeSelector = ({ theme, setTheme }) => {
  const themes = [
    { 
      id: 'cyberVault', 
      name: 'Cyber Vault', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="8" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 8V6a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      id: 'circuitBuilder', 
      name: 'Circuit Builder', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      id: 'cipherAdventure', 
      name: 'Cipher Adventure', 
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  return (
    <div className="theme-selector">
      <h3>Choose Your Theme</h3>
      <div className="theme-buttons">
        {themes.map(t => (
          <button
            key={t.id}
            className={`theme-btn ${theme === t.id ? 'active' : ''}`}
            onClick={() => setTheme(t.id)}
          >
            <span className="theme-icon">{t.icon}</span>
            <span>{t.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Theme 1: Cyber Vault Heist
const CyberVaultView = ({ level, timeRemaining, score, streak, hackerRank, onLevelComplete, onFail, onPlayerStateChange, playerState, setPlayerState }) => {
  const [switches, setSwitches] = useState([false, false, false]);
  const [verification, setVerification] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    if (playerState) {
      setSwitches(playerState);
    }
  }, [playerState]);

  const onToggle = (idx) => {
    const newSwitches = switches.map((v, i) => (i === idx ? !v : v));
    setSwitches(newSwitches);
    setPlayerState(newSwitches);
    onPlayerStateChange(newSwitches);
  };

  const onSubmit = () => {
    if (!switches.some(Boolean)) {
      setVerification({ status: 'rejected', message: 'No state set. Toggle at least one switch.' });
      return;
    }

    const isValid = checkWin(level, switches);
    
    if (!isValid) {
      setVerification({ status: 'rejected', message: 'Proof Rejected. Constraint not satisfied.' });
      onFail();
      return;
    }

    setVerification({ status: 'verifying', message: 'Verifying proof...' });
    
    setTimeout(() => {
      setVerification({ status: 'verified', message: 'ACCESS GRANTED' });
      celebrateSuccess();
      onLevelComplete();
    }, 800);
  };

  const onReset = () => {
    setSwitches([false, false, false]);
    setPlayerState([false, false, false]);
    onPlayerStateChange([false, false, false]);
    setVerification({ status: 'idle', message: '' });
  };

  const celebrateSuccess = () => {
    for (let i = 0; i < 20; i++) {
      createParticle(`ACCESS GRANTED`, "#10b981");
    }
  };

  const createParticle = (text, color) => {
    const particle = document.createElement('div');
    particle.textContent = text;
    particle.style.cssText = `
      position: fixed;
      color: ${color};
      font-weight: bold;
      font-size: 14px;
      pointer-events: none;
      z-index: 10000;
      left: ${50 + Math.random() * 20}%;
      top: ${50 + Math.random() * 20}%;
      animation: particleFloat 2s ease-out forwards;
    `;
    document.body.appendChild(particle);
    setTimeout(() => particle.remove(), 2000);
  };

  // Get level-specific instructions
  const getLevelInstructions = () => {
    switch (level) {
      case 1: return "Toggle the switches so that exactly ONE switch is ON";
      case 2: return "Toggle the switches so that exactly TWO switches are ON";
      case 3: return "Toggle the switches so that Left and Right are NOT both ON";
      case 4: return "Toggle the switches so that NOT all three switches are ON";
      case 5: return "Toggle the switches so that Middle is OFF, OR Left and Right have different states";
      case 6: return "Toggle the switches so that exactly TWO are ON, AND Right must be ON";
      default: return "Toggle switches Left, Middle, Right to satisfy the vault constraint";
    }
  };

  return (
    <div className="cyber-vault-theme">
      <div className="vault-instructions">
        <h3>Cyber Vault Heist - Level {level}</h3>
        <p className="instruction-text">{getLevelInstructions()}</p>
        <div className="instruction-hint">
          <strong>How to play:</strong> Click the Left, Middle, and Right switches to toggle them ON (green) or OFF (gray). 
          Find the combination that satisfies the constraint above, then click "Submit Proof".
              </div>
            </div>
      <div className="vault-door">
          <div className="switches">
            {['Left', 'Middle', 'Right'].map((label, idx) => (
              <div key={label} className={`switch ${switches[idx] ? 'on' : ''}`} onClick={() => onToggle(idx)}>
                <span className="label">{label[0]}</span>
                <div className="lever" />
              </div>
            ))}
          </div>
          <div className="console-panel">
            <div className="console-title">Verification Console</div>
            <div className={`console-text ${verification.status === 'verified' ? 'console-success' : verification.status === 'rejected' ? 'console-error' : ''}`}>
              {verification.status === 'idle' && 'AWAITING PROOF...'}
              {verification.status === 'verifying' && 'VERIFYING PROOF...'}
            {verification.status === 'verified' && verification.message}
              {verification.status === 'rejected' && verification.message}
            </div>
          </div>
        </div>
      <div className="maze-actions">
        <button className="prover-device" onClick={onSubmit} disabled={!switches.some(Boolean) || verification.status === 'verifying'}>
          Submit Proof
        </button>
        <button className="reset-button" onClick={onReset}>
          Reset
        </button>
      </div>
    </div>
  );
};

// Theme 2: Circuit Builder
const CircuitBuilderView = ({ level, timeRemaining, score, streak, hackerRank, onLevelComplete, onFail, onPlayerStateChange, playerState, setPlayerState }) => {
  const GATE_TYPES = {
    AND: { symbol: "∧", color: "#3b82f6" },
    OR:  { symbol: "∨", color: "#8b5cf6" },
    NOT: { symbol: "¬", color: "#ef4444" },
    XOR: { symbol: "⊕", color: "#f59e0b" }
  };

  const [gates, setGates] = useState([
    { id: 1, type: "AND", x: 100, y: 100 },
    { id: 2, type: "OR",  x: 300, y: 100 },
    { id: 3, type: "NOT", x: 200, y: 300 }
  ]);
  const [connections] = useState([]);
  const [draggedGate, setDraggedGate] = useState(null);
  const [inputs, setInputs] = useState([false, false, false]); // L, M, R inputs

  // For Circuit Builder, we'll use switches as inputs and gates to process them
  // The validation will check if the circuit output matches expected truth table
  useEffect(() => {
    if (playerState && Array.isArray(playerState)) {
      setInputs(playerState);
    }
  }, [playerState]);

  const handleGateDragStart = (e, gateId) => {
    setDraggedGate(gateId);
  };

  const handleGateDrag = (e, gateId) => {
    if (!draggedGate) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setGates(prev => prev.map(g => 
      g.id === gateId ? { ...g, x, y } : g
    ));
  };

  const handleGateDragEnd = () => {
    setDraggedGate(null);
  };

 
  const simulateCircuit = () => {
    
    return checkWin(level, inputs);
  };

  const onSubmit = () => {
    const isValid = simulateCircuit();
    
    if (!isValid) {
      onFail();
      return;
    }

    onLevelComplete();
  };

  const onToggleInput = (idx) => {
    const newInputs = inputs.map((v, i) => (i === idx ? !v : v));
    setInputs(newInputs);
    setPlayerState(newInputs);
    onPlayerStateChange(newInputs);
  };

  // Get level-specific instructions
  const getLevelInstructions = () => {
    switch (level) {
      case 1: return "Toggle the switches so that exactly ONE switch is ON";
      case 2: return "Toggle the switches so that exactly TWO switches are ON";
      case 3: return "Toggle the switches so that L and R are NOT both ON";
      case 4: return "Toggle the switches so that NOT all three switches are ON";
      case 5: return "Toggle the switches so that M is OFF, OR L and R have different states";
      case 6: return "Toggle the switches so that exactly TWO are ON, AND R must be ON";
      default: return "Toggle switches L, M, R to satisfy the circuit constraint";
    }
  };

        return (
    <div className="circuit-builder-theme">
      <div className="circuit-instructions">
        <h3>Circuit Builder - Level {level}</h3>
        <p className="instruction-text">{getLevelInstructions()}</p>
        <div className="instruction-hint">
          <strong>How to play:</strong> Click the L, M, R switches to turn them ON (green) or OFF (gray). 
          Find the combination that satisfies the constraint above, then click "Test Circuit".
        </div>
            </div>
            
      <div className="circuit-board" 
           onMouseMove={(e) => draggedGate && handleGateDrag(e, draggedGate)}
           onMouseUp={handleGateDragEnd}
           onMouseLeave={handleGateDragEnd}>
        <svg width="100%" height="400" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
              <polygon points="0 0, 10 3, 0 6" fill="#475569" />
            </marker>
          </defs>
          {connections.map((conn, i) => (
            <line
              key={i}
              x1={conn.fromX}
              y1={conn.fromY}
              x2={conn.toX}
              y2={conn.toY}
              className={`wire ${conn.active ? 'active' : ''}`}
            />
          ))}
        </svg>
        
        {/* Input switches */}
        <div className="circuit-inputs">
          {['L', 'M', 'R'].map((label, idx) => (
            <div key={label} className={`circuit-input ${inputs[idx] ? 'on' : ''}`} onClick={() => onToggleInput(idx)}>
              <span className="input-label">{label}</span>
              <div className="input-indicator" />
              <span className="input-status">{inputs[idx] ? 'ON' : 'OFF'}</span>
            </div>
          ))}
            </div>

        {/* Logic gates - shown for visual context but not interactive yet */}
        {gates.map(gate => (
          <div
            key={gate.id}
            className="gate-node"
            style={{
              left: `${gate.x}px`,
              top: `${gate.y}px`,
              borderColor: GATE_TYPES[gate.type].color,
              backgroundColor: `${GATE_TYPES[gate.type].color}20`
            }}
            onMouseDown={(e) => handleGateDragStart(e, gate.id)}
          >
            <div style={{ color: GATE_TYPES[gate.type].color, fontSize: '24px', fontWeight: 'bold' }}>
              {GATE_TYPES[gate.type].symbol}
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>{gate.type}</div>
          </div>
        ))}
            </div>

      <div className="circuit-actions">
        <button className="prover-device" onClick={onSubmit}>
          Test Circuit
        </button>
      </div>
            </div>
  );
};

// Theme 3: Cipher Decryption Adventure (placeholder - will be fully implemented)
const CipherAdventureView = ({ level, timeRemaining, score, streak, hackerRank, onLevelComplete, onFail, onPlayerStateChange, playerState, setPlayerState }) => {
  const STORY_FRAGMENTS = {
    1: "AGENT X: The vault requires... [FIRST CLUE]",
    2: "...a dual authentication system. Both...",
    3: "...cannot be active simultaneously or...",
    4: "...triple activation triggers lockdown...",
    5: "...middle switch controls the...",
    6: "...final combination requires exactly two..."
  };

  const [switches, setSwitches] = useState([false, false, false]);
  const [verification, setVerification] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    if (playerState) {
      setSwitches(playerState);
    }
  }, [playerState]);

  const onToggle = (idx) => {
    const newSwitches = switches.map((v, i) => (i === idx ? !v : v));
    setSwitches(newSwitches);
    setPlayerState(newSwitches);
    onPlayerStateChange(newSwitches);
  };

  const onSubmit = () => {
    const isValid = checkWin(level, switches);
    
    if (!isValid) {
      setVerification({ status: 'rejected', message: 'Decryption Failed' });
      onFail();
      return;
    }

    setVerification({ status: 'verifying', message: 'Decrypting...' });
    
    setTimeout(() => {
      setVerification({ status: 'verified', message: 'DECRYPTED' });
      onLevelComplete();
    }, 800);
  };

  return (
    <div className="cipher-adventure-theme">
      <div className="encrypted-message">
        <div className="decryption-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(level / TOTAL_LEVELS) * 100}%` }} />
          </div>
          <div className="decryption-text">
            {STORY_FRAGMENTS[level]?.split("").map((char, i) => (
              <span key={i} className={i < level * 5 ? "decrypted" : "encrypted"}>
                {char}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="cipher-controls">
        <div className="switches">
          {['Left', 'Middle', 'Right'].map((label, idx) => (
            <div key={label} className={`switch ${switches[idx] ? 'on' : ''}`} onClick={() => onToggle(idx)}>
              <span className="label">{label[0]}</span>
              <div className="lever" />
            </div>
          ))}
            </div>
        <button className="prover-device" onClick={onSubmit} disabled={verification.status === 'verifying'}>
          Decrypt
              </button>
            </div>
      {verification.status !== 'idle' && (
        <div className={`verifier ${verification.status === 'verified' ? 'success-msg' : 'error-msg'}`}>
          {verification.message}
        </div>
            )}
          </div>
        );
};

// Main LogicMaze Component
const LogicMaze = () => {
  const { updateGameProgress, getProgress } = useProgress();
  
  // Core game state
  const [level, setLevel] = useState(1);
  const [theme, setTheme] = useState("cyberVault");
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [hackerRank, setHackerRank] = useState("Script Kiddie");
  const [playerState, setPlayerState] = useState([false, false, false]);
  const [gameStarted, setGameStarted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [completed, setCompleted] = useState(false);
  
  const timerRef = useRef(null);
  const levelStartTimeRef = useRef(Date.now());

  // Load saved progress
  useEffect(() => {
    try {
      const saved = getProgress('games', 'logic-maze');
      if (saved && typeof saved.level === 'number') {
        const completedLevels = saved.level;
        const nextLevel = Math.min(completedLevels + 1, TOTAL_LEVELS);
        setLevel(nextLevel);
        if (nextLevel > TOTAL_LEVELS) {
          setCompleted(true);
        }
      }
    } catch (_) {
      /* no-op */
    }
  }, [getProgress]);

  // Timer system
  useEffect(() => {
    if (!gameStarted || completed) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  
  }, [gameStarted, completed]);

  // Update rank when streak changes
  useEffect(() => {
    setHackerRank(getRankFromStreak(streak));
  }, [streak]);

  const handleTimeout = () => {
    setStreak(0);
    setTimeRemaining(60);
    // Could show timeout message
  };

  const handleLevelComplete = useCallback(() => {
    const timeTaken = 60 - timeRemaining;
    const timeBonus = Math.max(0, (60 - timeTaken) * 10); // More time remaining = higher bonus
    const streakBonus = streak * 50;
    const levelScore = 100 + timeBonus + streakBonus;
    
    const newScore = score + levelScore;
    const newStreak = streak + 1;
    
    setScore(newScore);
    setStreak(newStreak);
    
    const isFinalLevel = level >= TOTAL_LEVELS;
    
    if (isFinalLevel) {
      updateGameProgress('logic-maze', newScore, TOTAL_LEVELS, true, 'Secure Receipts');
      setCompleted(true);
    } else {
      updateGameProgress('logic-maze', newScore, level, false, 'Secure Receipts');
      setLevel(prev => prev + 1);
      setTimeRemaining(60);
      levelStartTimeRef.current = Date.now();
      setPlayerState([false, false, false]);
    }
  }, [level, timeRemaining, streak, score, updateGameProgress]);

  const handleFail = useCallback(() => {
    setStreak(0);
    setTimeRemaining(60);
  }, []);

  const handlePlayerStateChange = useCallback((newState) => {
    setPlayerState(newState);
  }, []);

  const startGame = () => {
    setGameStarted(true);
    setShowIntro(false);
    levelStartTimeRef.current = Date.now();
  };

  const sharedProps = {
    level,
    TOTAL_LEVELS,
    timeRemaining,
    score,
    streak,
    hackerRank,
    onLevelComplete: handleLevelComplete,
    onFail: handleFail,
    onPlayerStateChange: handlePlayerStateChange,
    playerState,
    setPlayerState
  };

  if (showIntro) {
    return (
      <div className="logic-maze">
        <div className="sq-modal-overlay" role="dialog" aria-modal="true">
          <div className="sq-modal">
            <div className="sq-modal-header">
              <h2>Logic Maze</h2>
              <button className="sq-close" onClick={() => setShowIntro(false)} aria-label="Close">×</button>
            </div>
            <div className="sq-modal-body">
              <p>Master zero-knowledge proofs through interactive puzzles. Choose your theme and prove your knowledge!</p>
              <ThemeSelector theme={theme} setTheme={setTheme} />
            </div>
            <div className="sq-modal-footer">
              <button className="sq-primary" onClick={startGame}>Start Game →</button>
            </div>
            </div>
          </div>
        </div>
    );
  }

  if (completed) {
    return (
      <div className="logic-maze">
        <div className="completion-certificate">
          <div className="certificate-header">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <h2>Congratulations!</h2>
          </div>
          <div className="certificate-stats">
            <div className="stat">
              <div className="stat-label">Final Score</div>
              <div className="stat-value">{score}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Final Rank</div>
              <div className="stat-value rank-badge">{hackerRank}</div>
              </div>
            <div className="stat">
              <div className="stat-label">Best Streak</div>
              <div className="stat-value">{streak}</div>
          </div>
        </div>
          <p className="certificate-message">You've mastered the Logic Maze! You've proven your knowledge of zero-knowledge proofs.</p>
      </div>
      </div>
    );
  }

  return (
    <div className="logic-maze">
      <div className="game-header-bar">
        <div className="header-left">
          <h2>Logic Maze - {theme === 'cyberVault' ? 'Cyber Vault' : theme === 'circuitBuilder' ? 'Circuit Builder' : 'Cipher Adventure'}</h2>
          <div className="level-info">Level {level} of {TOTAL_LEVELS}</div>
        </div>
        <div className="header-right">
          <div className="game-stats">
            <div className="stat-item">
              <span className="stat-label">Time</span>
              <span className={`stat-value ${timeRemaining < 10 ? 'time-warning' : ''}`}>{timeRemaining}s</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Score</span>
              <span className="stat-value">{score}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Streak</span>
              <span className="stat-value">{streak}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Rank</span>
              <span className="stat-value rank-badge">{hackerRank}</span>
            </div>
          </div>
        </div>
      </div>

      <ThemeSelector theme={theme} setTheme={setTheme} />

      {theme === "cyberVault" && <CyberVaultView {...sharedProps} />}
      {theme === "circuitBuilder" && <CircuitBuilderView {...sharedProps} />}
      {theme === "cipherAdventure" && <CipherAdventureView {...sharedProps} />}
    </div>
  );
};

export default LogicMaze;
